import type { Configuration, RedirectRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';
import { env } from './env';

/**
 * MSAL Configuration for Microsoft Entra ID (Azure AD)
 *
 * Configuration is now driven by environment variables:
 * - VITE_AZURE_CLIENT_ID: Azure AD Application (Client) ID
 * - VITE_AZURE_TENANT_ID: Azure AD Directory (Tenant) ID
 * - VITE_AUTH_REDIRECT_URI: OAuth redirect URI
 *
 * These should be configured in .env file for each environment
 */

/**
 * Get MSAL configuration from environment variables
 */
function getMsalConfig() {
  return {
    clientId: env.VITE_AZURE_CLIENT_ID,
    tenantId: env.VITE_AZURE_TENANT_ID,
    redirectUri: env.VITE_AUTH_REDIRECT_URI,
  };
}

const MSAL_CONFIG = getMsalConfig();

/**
 * MSAL Browser Configuration
 * Using redirect flow (best practice for production)
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CONFIG.clientId,
    authority: `https://login.microsoftonline.com/${MSAL_CONFIG.tenantId}`,
    redirectUri: MSAL_CONFIG.redirectUri,
    postLogoutRedirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for persistence across browser sessions
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            // Info level logging disabled to comply with linting rules
            return;
          case LogLevel.Verbose:
            // Verbose level logging disabled to comply with linting rules
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Info, // Set to LogLevel.Verbose for debugging
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes for login request
 * - openid: Required for OpenID Connect
 * - profile: Access to user profile information
 * - email: Access to user email
 */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
  prompt: 'select_account', // Show account picker to choose which Microsoft account to use
  // Note: This shows account picker but won't require password if already signed in with that account
};

/**
 * Scopes for silent token acquisition
 */
export const silentRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Protected API scopes (add your backend API scopes here when needed)
 * Example: `api://${clientId}/access_as_user`
 */
export const apiRequest = {
  scopes: [], // Add API scopes when integrating with backend
};
